import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { updateTask } from 'src/careflow/care-coordination/task/main/task.api';
import { onSuccess } from 'src/@types/crs/case';
import {
  taskToAnnotations,
  annotationsToPayload,
  TaskAnnotationItem,
} from './task-annotation-utils';
import TaskAnnotationsEditor from './TaskAnnotationsEditor';
import CancelButton from 'src/careflow/common/CancelButton';
import Iconify from 'src/components/Iconify';

export interface TaskAnnotationsPanelProps {
  open: boolean;
  onClose: () => void;
  task: WrappedTask | null;
  taskId: string | undefined;
  onSuccesfulEdit?: onSuccess;
}

export default function TaskAnnotationsPanel({
  open,
  onClose,
  task,
  taskId,
  onSuccesfulEdit,
}: TaskAnnotationsPanelProps) {
  const { i18n } = useLocales();
  const currentUser = useAuth()?.getCurrentUser?.();
  const currentUserName = currentUser?.name ?? '';
  const [annotations, setAnnotations] = useState<TaskAnnotationItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [triggerCreateMode, setTriggerCreateMode] = useState(false);
  const prevOpenRef = useRef(false);
  const lastLoadedTaskIdRef = useRef<string | undefined>(undefined);

  // Load from task only when first opening for this task (or when switching to a different task). Do NOT overwrite when reopening the same task so per-annotation dates (current date for new/edited) are preserved.
  useEffect(() => {
    if (!open || !task) {
      prevOpenRef.current = open;
      return;
    }
    const justOpened = !prevOpenRef.current;
    prevOpenRef.current = open;
    const taskIdCurrent = taskId ?? (task as any)?.id;
    const taskChanged = lastLoadedTaskIdRef.current !== taskIdCurrent;
    if (justOpened && (annotations.length === 0 || taskChanged)) {
      setAnnotations(taskToAnnotations(task, currentUserName));
      lastLoadedTaskIdRef.current = taskIdCurrent;
    }
  }, [open, task, currentUserName, taskId, annotations.length]);

  const handleSave = useCallback(async () => {
    const payload = annotationsToPayload(annotations);
    if (!payload || !taskId) return;
    setIsSaving(true);
    const response = await updateTask(payload, taskId);
    setIsSaving(false);
    if (!response) return;
    onSuccesfulEdit?.(response);
    onClose();
  }, [annotations, taskId, onSuccesfulEdit, onClose]);

  const canSave = !!annotationsToPayload(annotations);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '85vw',
          maxWidth: 1200,
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" component="span">
            {i18n('annotations.details.title', 'crs')}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setTriggerCreateMode(true)}
            disabled={isSaving}
          >
            {i18n('annotations.details.newAnnotationButton', 'crs')}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 360 }}>
        <TaskAnnotationsEditor
          annotations={annotations}
          onAnnotationsChange={setAnnotations}
          task={task}
          disabled={isSaving}
          showHeader={false}
          triggerCreateMode={triggerCreateMode}
          onCreateModeTriggered={() => setTriggerCreateMode(false)}
        />
      </DialogContent>

      <DialogActions>
        <CancelButton onClick={onClose} disabled={isSaving} />
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !canSave}
        >
          {isSaving ? 'Saving…' : i18n('saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
